import type { Outline, OutlineItem } from "@op/puck-types";
import { Dc_Reading } from "@op/sf-op";
import { useEffect, useRef, useState } from "react";

export interface ReadingCompanionOutlineProps {
  outline: Outline;
}

const ReadingCompanionOutlineItem = ({ item }: { item: OutlineItem }) => {
  const observer = useRef<IntersectionObserver | null>(null);
  if (!item.content) return null;

  const [isScrolledPast, setIsScrolledPast] = useState(false);
  const [isActive, setIsActive] = useState(false);

  function handleIntersection(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setIsActive(true);
        setIsScrolledPast(false);
      } else {
        setIsActive(false);
        setIsScrolledPast(entry.boundingClientRect.top < 0);
      }
    });
  }

  useEffect(() => {
    const target = document.getElementById(item.slug);
    if (!target) {
      console.warn(`Target element with slug ${item.slug} not found`);
      return;
    }

    observer.current = new IntersectionObserver(handleIntersection, {
      rootMargin: "20% 0px 20% 0px",
      threshold: 1.0,
    });
    observer.current.observe(target);

    return () => {
      if (observer.current) {
        observer.current.disconnect();
        observer.current = null;
      }
    };
  }, []);

  return (
    <a href={`#${item.slug}`} className="contents">
      <Dc_Reading.IndexItem
        layer={item.level === "excursion" ? "subsection" : item.level}
        text={item.content}
        variant={
          isActive ? "active" : isScrolledPast ? "scrolled-past" : "inactive"
        }
        isExcursion={item.isExcursion}
      />
    </a>
  );
};

export const ReadingCompanionOutline = ({
  outline,
}: ReadingCompanionOutlineProps) => {
  return (
    <Dc_Reading.IndexItemStack>
      {outline?.map((item, index) => (
        <ReadingCompanionOutlineItem
          key={`${item.slug}-${index}`}
          item={item}
        />
      ))}
    </Dc_Reading.IndexItemStack>
  );
};
